// BnB per-customer buy-limit enforcement, ported from Live (Launchpad) theme.
//
// Behavior: products tagged with `limit:N` (e.g. `limit:5`) cap the
// total purchasable quantity per customer at N. The limit is enforced
// client-side by reading cart contents, disabling Add-to-Cart / Update
// buttons when the user would exceed the cap, and showing an inline
// warning message.
//
// Wiring: Liquid emits `data-bnb-buy-limit="N"` on relevant elements
// (product form on PDP, line-item rows in cart/drawer). This file
// queries those data attributes — no per-element JS needed in templates.
//
// Concept-theme specifics:
// - PDP: <quantity-input> web component wraps the qty input; ATC is
//   <button type="submit" name="add" form="...">. Limit message reuses
//   Concept's existing .product-form__error-message element.
// - Cart / Cart drawer: each line item is a <cart-item> custom element
//   (or .cart-item row). Update button is <button name="update">.

(function () {
  'use strict';

  // Cached cart totals so we don't refetch /cart.js on every input event.
  // Invalidated when a fetch resolves and after any cart mutation event
  // fires from Concept's cart components.
  var cartCache = null;
  var cartCachePromise = null;

  function fetchCart() {
    if (cartCachePromise) return cartCachePromise;
    cartCachePromise = fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { cartCache = cart; cartCachePromise = null; return cart; })
      .catch(function () { cartCachePromise = null; return null; });
    return cartCachePromise;
  }

  function getCartCached() {
    return cartCache ? Promise.resolve(cartCache) : fetchCart();
  }

  // Concept dispatches 'cart:update' (and Shopify's cart Section
  // Rendering API resolves with the latest cart). Listen broadly so the
  // cache stays fresh after any ATC, qty-change, or remove.
  ['cart:update', 'cart:refresh', 'shopify:cart:update'].forEach(function (evt) {
    document.addEventListener(evt, function () { cartCache = null; });
  });

  // Total quantity of a given product (across all variants) currently in cart.
  function totalInCart(cart, productId) {
    if (!cart || !cart.items) return 0;
    var total = 0;
    cart.items.forEach(function (item) {
      if (String(item.product_id) === String(productId)) {
        total += item.quantity;
      }
    });
    return total;
  }

  // ---------- PDP enforcement ---------------------------------------

  function enforcePDP(form) {
    var limit = parseInt(form.getAttribute('data-bnb-buy-limit'), 10) || 0;
    if (limit < 1) return;
    var productId = form.getAttribute('data-bnb-product-id');
    if (!productId) return;

    var qtyInput = form.querySelector('input[name="quantity"]');
    var minusBtn = form.querySelector('button[name="minus"]');
    var plusBtn = form.querySelector('button[name="plus"]');
    var atcBtn = form.querySelector('button[type="submit"][name="add"], button[type="submit"]:not([name])');
    // Fall back to a button outside the form (Concept renders the ATC
    // outside <product-form> but linked via the `form` attribute).
    if (!atcBtn) {
      var formId = form.id;
      if (formId) atcBtn = document.querySelector('button[form="' + formId + '"][name="add"], button[form="' + formId + '"][type="submit"]');
    }
    var errEl = form.querySelector('.product-form__error-message') ||
                document.querySelector('.product-form__error-message');

    if (!qtyInput) return;

    function showMessage(text) {
      if (!errEl) return;
      errEl.textContent = text;
      errEl.hidden = false;
      errEl.style.display = '';
    }
    function hideMessage() {
      if (!errEl) return;
      errEl.hidden = true;
      errEl.textContent = '';
    }
    function setDisabled(el, state) {
      if (!el) return;
      if (state) {
        el.setAttribute('disabled', 'disabled');
        el.disabled = true;
      } else {
        el.removeAttribute('disabled');
        el.disabled = false;
      }
    }

    // Special handling for the ATC button. Concept's CSS toggles a
    // "Sold Out - Notify me" alternate button whenever the ATC is
    // [disabled], assuming the disable means out-of-stock. That swap is
    // suppressed by an [unavailable] attribute (see theme.css line
    // ~12040: `.product-form__submit[disabled]:not([loading], [unavailable])`).
    // We add [unavailable] to keep the ATC visible but non-functional.
    function setAtcBlocked(el, state) {
      if (!el) return;
      if (state) {
        el.setAttribute('disabled', 'disabled');
        el.setAttribute('unavailable', '');
        el.disabled = true;
      } else {
        el.removeAttribute('disabled');
        el.removeAttribute('unavailable');
        el.disabled = false;
      }
    }

    function recheck() {
      getCartCached().then(function (cart) {
        var alreadyIn = totalInCart(cart, productId);
        var remaining = Math.max(0, limit - alreadyIn);

        var desired = parseInt(qtyInput.value, 10);
        if (isNaN(desired) || desired < 1) desired = 1;

        // Always keep the qty input + minus button enabled so users can
        // correct downward when over-limit. Only disable when there's
        // truly nothing they can add (remaining = 0) AND no decreasing
        // helps — but since the input is for "how many to add" not
        // "qty in cart", even remaining=0 doesn't justify disabling
        // qty controls. Leave them enabled in all cases.
        setDisabled(qtyInput, false);

        if (remaining <= 0) {
          showMessage('You’ve already added the maximum of this product (' + limit + ') to your cart.');
          setAtcBlocked(atcBtn, true);
          setDisabled(plusBtn, true);
          // Allow minus only if desired > 1 so user can still adjust.
          setDisabled(minusBtn, desired <= 1);
          return;
        }

        if (desired > remaining) {
          // Wording adapts based on whether they already have any in
          // cart. "10 more (max 10 total)" reads as duplicative when
          // nothing's in cart yet; a plain per-product limit message
          // is clearer in that case. Including "of this product" makes
          // it clear which product the cap applies to when a cart
          // contains multiple limited items.
          var msg;
          if (alreadyIn > 0) {
            msg = 'You can only add ' + remaining + ' more of this product (max ' + limit + ' per customer).';
          } else {
            msg = 'Purchases of this product are limited to ' + limit + ' per customer.';
          }
          showMessage(msg);
          setAtcBlocked(atcBtn, true);
        } else {
          hideMessage();
          setAtcBlocked(atcBtn, false);
        }

        setDisabled(plusBtn, desired >= remaining);
        setDisabled(minusBtn, desired <= 1);
      });
    }

    // Listen on the form so we capture qty input changes from typing
    // and from Concept's <quantity-input> +/- button clicks (which
    // dispatch native input/change events on the underlying input).
    form.addEventListener('input', function (e) {
      if (e.target === qtyInput) recheck();
    });
    form.addEventListener('change', function (e) {
      if (e.target === qtyInput) recheck();
    });
    form.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('button[name="plus"], button[name="minus"]');
      if (btn) setTimeout(recheck, 100);
    });

    // Block submit if ATC is disabled.
    form.addEventListener('submit', function (e) {
      if (atcBtn && atcBtn.disabled) {
        e.preventDefault();
        e.stopPropagation();
      }
    });

    // Initial check (defer so layout settles + cart fetch can begin).
    setTimeout(recheck, 100);
  }

  // ---------- cart-line enforcement (drawer + cart page) ------------

  // Each line item is marked with data-bnb-buy-limit + data-bnb-product-id.
  // We disable the line's update button and qty input when the cart
  // total for that product exceeds the limit.
  function enforceCartLines() {
    var lines = document.querySelectorAll('[data-bnb-buy-limit][data-bnb-product-id]');
    if (!lines.length) return;
    getCartCached().then(function (cart) {
      lines.forEach(function (line) {
        var limit = parseInt(line.getAttribute('data-bnb-buy-limit'), 10) || 0;
        var productId = line.getAttribute('data-bnb-product-id');
        if (limit < 1 || !productId) return;

        var inCart = totalInCart(cart, productId);
        var overLimit = inCart > limit;

        var qtyInput = line.querySelector('input[name="quantity"], input[name^="updates["]');
        var plusBtn = line.querySelector('button[name="plus"]');
        var msg = line.querySelector('.bnb-buy-limit-message');

        if (overLimit) {
          if (qtyInput) {
            qtyInput.setAttribute('aria-invalid', 'true');
          }
          if (plusBtn) {
            plusBtn.setAttribute('disabled', 'disabled');
            plusBtn.disabled = true;
          }
          if (msg) {
            msg.textContent = 'Purchases limited to ' + limit + ' per customer';
            msg.hidden = false;
          }
        } else {
          if (qtyInput) qtyInput.removeAttribute('aria-invalid');
          if (plusBtn && !plusBtn.dataset.bnbHardDisabled) {
            plusBtn.removeAttribute('disabled');
            plusBtn.disabled = false;
          }
          if (msg) msg.hidden = true;
        }
      });
    });
  }

  // Run cart-line enforcement on load and whenever cart updates.
  function initCartLines() {
    enforceCartLines();
  }
  ['cart:update', 'cart:refresh', 'shopify:cart:update'].forEach(function (evt) {
    document.addEventListener(evt, function () { setTimeout(enforceCartLines, 50); });
  });

  // ---------- init --------------------------------------------------

  function initPDPForms() {
    document.querySelectorAll('form[data-bnb-buy-limit][data-bnb-product-id]').forEach(enforcePDP);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initPDPForms();
      initCartLines();
    });
  } else {
    initPDPForms();
    initCartLines();
  }
})();
